/* BUJO mode for the calendar — pastel paper, spiral binding, sticky-note sidebar,
 * habits tracker grid, auto-stickers per event title.
 *
 * BujoSidebar  — sticky-note panels around MonthView
 * useSticker   — cached event-title → emoji
 * BUJO_PAPER_CLASS / BUJO_SPIRAL_CLASS — visual skin */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Pin, Edit3, BookOpen, Heart, Sparkles, Star, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAPER_BG = 'linear-gradient(135deg, #FFF7F0 0%, #FFF0F3 50%, #F2F7FF 100%)';

// Frontend keyword cache (instant render before backend roundtrip)
const FRONT_MAP = [
  [/(yoga|stretch)/i, '🧘‍♀️'],
  [/(gym|träna|workout|löpning|cardio|cykl)/i, '💪'],
  [/(lunch|brunch|fika|kaffe|coffee|frukost)/i, '☕'],
  [/(middag|dinner|restaurang)/i, '🍝'],
  [/(dejt|date)/i, '💕'],
  [/(tand|dentist)/i, '🦷'],
  [/(läk|doctor|sjuk|vård)/i, '🩺'],
  [/(film|movie|bio)/i, '🎬'],
  [/(läs|read|bok)/i, '📖'],
  [/(meditat)/i, '🌙'],
  [/(städ|cleaning)/i, '🧹'],
  [/(handla|grocery|inköp|mat)/i, '🛒'],
  [/(party|fest|födelsedag|birthday)/i, '🎉'],
  [/(jobb|möte|meeting|work|deadline)/i, '💼'],
  [/(plant|trädgård|garden|odl)/i, '🪴'],
  [/(måla|paint|kreativ|konst)/i, '🎨'],
  [/(musik|music|spela)/i, '🎵'],
  [/(rest|vila|sömn|nap)/i, '😴'],
  [/(resa|travel|flyg|tåg|semester)/i, '✈️'],
  [/(famil|mamma|pappa|syster|bror)/i, '🌸'],
  [/(shopping|köp|kläder)/i, '🛍️'],
  [/(spa|massage|skin|smink|makeup)/i, '💆‍♀️'],
  [/(foto|photo|kamera|content)/i, '📸'],
  [/(plan|review|recap)/i, '📋'],
];

const STICKER_LOCAL_KEY = 'bujo_sticker_cache_v1';

function loadStickerCache() {
  try { return JSON.parse(localStorage.getItem(STICKER_LOCAL_KEY) || '{}'); } catch { return {}; }
}
function saveStickerCache(c) {
  try { localStorage.setItem(STICKER_LOCAL_KEY, JSON.stringify(c)); } catch {/**/}
}

const _stickerCache = loadStickerCache();
const _inflight = {};

export function useSticker(title) {
  const [emoji, setEmoji] = useState(() => {
    const t = (title || '').trim().toLowerCase();
    if (!t) return '✨';
    if (_stickerCache[t]) return _stickerCache[t];
    for (const [re, e] of FRONT_MAP) if (re.test(t)) return e;
    return null;
  });

  useEffect(() => {
    const t = (title || '').trim().toLowerCase();
    if (!t) { setEmoji('✨'); return; }
    if (_stickerCache[t]) { setEmoji(_stickerCache[t]); return; }
    for (const [re, e] of FRONT_MAP) if (re.test(t)) { setEmoji(e); _stickerCache[t] = e; saveStickerCache(_stickerCache); return; }
    if (emoji) return;   // already resolved via front-cache
    if (_inflight[t]) {
      _inflight[t].then((e) => setEmoji(e)).catch(() => setEmoji('✨'));
      return;
    }
    _inflight[t] = axios
      .get(`${API}/bujo-cal/sticker`, { params: { title }, withCredentials: true })
      .then((r) => {
        const e = r.data?.emoji || '✨';
        _stickerCache[t] = e;
        saveStickerCache(_stickerCache);
        return e;
      })
      .finally(() => { delete _inflight[t]; });
    _inflight[t].then((e) => setEmoji(e)).catch(() => setEmoji('✨'));
  }, [title, emoji]);

  return emoji || '✨';
}

function StickyNote({ color = 'rose', tape = 'pink', title, children, testid }) {
  const palette = {
    rose:   'bg-rose-50 text-rose-800 border-rose-200',
    pink:   'bg-pink-50 text-pink-800 border-pink-200',
    yellow: 'bg-yellow-50 text-yellow-900 border-yellow-200',
    violet: 'bg-violet-50 text-violet-800 border-violet-200',
    sky:    'bg-sky-50 text-sky-800 border-sky-200',
    mint:   'bg-emerald-50 text-emerald-800 border-emerald-200',
  };
  const tapeColor = {
    pink: 'bg-pink-300', mint: 'bg-emerald-300', yellow: 'bg-yellow-300',
    sky: 'bg-sky-300', violet: 'bg-violet-300', rose: 'bg-rose-300',
  };
  return (
    <div
      className={`relative rounded-lg border-2 shadow-md p-3 ${palette[color] || palette.rose} transform rotate-[-1.5deg] hover:rotate-0 transition-transform`}
      style={{ boxShadow: '4px 4px 10px rgba(0,0,0,0.07)' }}
      data-testid={testid}
    >
      <div className={`absolute -top-2 left-1/2 -translate-x-1/2 w-12 h-3 ${tapeColor[tape] || tapeColor.pink} opacity-70 rounded-sm`} />
      {title && (
        <div className="text-[10px] uppercase tracking-widest font-extrabold mb-1.5 text-center opacity-80">
          {title}
        </div>
      )}
      <div className="text-xs">{children}</div>
    </div>
  );
}

function EditableList({ items, onSave, max = 8, placeholder = '...', emoji = '☆' }) {
  const [draft, setDraft] = useState(items || []);
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (!editing) setDraft(items || []); }, [items, editing]);

  const save = () => {
    const cleaned = draft.map((s) => (s || '').trim()).slice(0, max);
    onSave(cleaned);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div onClick={() => setEditing(true)} className="cursor-pointer hover:bg-white/60 rounded p-1 -m-1 space-y-0.5">
        {(items || []).filter(Boolean).slice(0, max).map((s, i) => (
          <div key={i} className="flex items-start gap-1 text-[11px]"><span className="opacity-60">{emoji}</span><span>{s}</span></div>
        ))}
        {(items || []).filter(Boolean).length === 0 && (
          <div className="text-[10px] italic opacity-50 flex items-center gap-1"><Edit3 className="w-2.5 h-2.5" /> klicka för att redigera</div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1" onClick={(e) => e.stopPropagation()}>
      {Array.from({ length: max }).map((_, i) => (
        <input
          key={i}
          value={draft[i] || ''}
          onChange={(e) => { const next = [...draft]; next[i] = e.target.value; setDraft(next); }}
          placeholder={`${i + 1}. ${placeholder}`}
          maxLength={120}
          className="w-full bg-white/70 border border-current/20 rounded px-1.5 py-0.5 text-[11px] outline-none focus:bg-white"
        />
      ))}
      <div className="flex gap-1.5">
        <button onClick={save} className="flex-1 text-[10px] py-1 rounded bg-current/80 text-white font-bold">Spara</button>
        <button onClick={() => { setDraft(items || []); setEditing(false); }} className="flex-1 text-[10px] py-1 rounded bg-white border-2 border-current/40 font-bold">Avbryt</button>
      </div>
    </div>
  );
}

function EditableText({ value, onSave, placeholder = 'klicka för att skriva…', rows = 3 }) {
  const [draft, setDraft] = useState(value || '');
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (!editing) setDraft(value || ''); }, [value, editing]);

  if (!editing) {
    return (
      <div onClick={() => setEditing(true)} className="cursor-pointer hover:bg-white/60 rounded p-1 -m-1 min-h-[40px]">
        {value ? <p className="text-[11px] whitespace-pre-wrap">{value}</p> : <p className="text-[10px] italic opacity-50">{placeholder}</p>}
      </div>
    );
  }
  return (
    <div onClick={(e) => e.stopPropagation()} className="space-y-1">
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={rows}
        maxLength={500}
        autoFocus
        className="w-full bg-white/70 border border-current/20 rounded px-1.5 py-1 text-[11px] outline-none focus:bg-white resize-none"
      />
      <div className="flex gap-1.5">
        <button onClick={() => { onSave(draft.trim()); setEditing(false); }} className="flex-1 text-[10px] py-1 rounded bg-current/80 text-white font-bold">Spara</button>
        <button onClick={() => { setDraft(value || ''); setEditing(false); }} className="flex-1 text-[10px] py-1 rounded bg-white border-2 border-current/40 font-bold">Avbryt</button>
      </div>
    </div>
  );
}

function HabitsTracker({ month, sidebar, refresh }) {
  const habits = sidebar.habits || [];
  const log = sidebar.habits_log || {};
  const [adding, setAdding] = useState('');
  const [year, mo] = month.split('-').map(Number);
  const daysInMonth = new Date(year, mo, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const colors = ['#F87171', '#FB923C', '#FBBF24', '#A3E635', '#34D399', '#60A5FA', '#A78BFA', '#F472B6'];

  const addHabit = async () => {
    const name = adding.trim().slice(0, 30);
    if (!name) return;
    const next = [...habits, name].slice(0, 8);
    await axios.post(`${API}/bujo-cal/sidebar`, { month, key: 'habits', value: next }, { withCredentials: true });
    setAdding('');
    refresh();
  };

  const removeHabit = async (name) => {
    const next = habits.filter((h) => h !== name);
    await axios.post(`${API}/bujo-cal/sidebar`, { month, key: 'habits', value: next }, { withCredentials: true });
    refresh();
  };

  const toggle = async (name, dayNum) => {
    const date = `${month}-${String(dayNum).padStart(2, '0')}`;
    const current = !!log?.[name]?.[date];
    await axios.post(`${API}/bujo-cal/habits/toggle`, { month, habit: name, date, value: !current }, { withCredentials: true });
    refresh();
  };

  return (
    <StickyNote color="mint" tape="mint" title="Habits Tracker" testid="bujo-habits">
      {habits.length === 0 && <p className="text-[10px] italic opacity-50 mb-1">Lägg till din första vana →</p>}
      <div className="space-y-1 max-h-[200px] overflow-y-auto pr-1">
        {habits.map((h, hi) => (
          <div key={h} className="flex items-center gap-1">
            <div className="text-[10px] font-bold w-16 flex-shrink-0 truncate flex items-center gap-0.5 group">
              <span>{h}</span>
              <button onClick={() => removeHabit(h)} className="opacity-0 group-hover:opacity-50 hover:opacity-100" title="Ta bort"><X className="w-2.5 h-2.5" /></button>
            </div>
            <div className="flex gap-[1px] flex-1 overflow-x-auto">
              {days.map((d) => {
                const date = `${month}-${String(d).padStart(2, '0')}`;
                const on = !!log?.[h]?.[date];
                return (
                  <button
                    key={d}
                    onClick={() => toggle(h, d)}
                    title={`Dag ${d}`}
                    className="w-3 h-3 flex-shrink-0 rounded-sm border border-emerald-200 hover:scale-125 transition-transform"
                    style={{ backgroundColor: on ? colors[hi % colors.length] : 'white' }}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {habits.length < 8 && (
        <div className="flex gap-1 mt-2">
          <input value={adding} onChange={(e) => setAdding(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addHabit()} placeholder="+ ny vana" maxLength={30} className="flex-1 bg-white/70 border border-emerald-200 rounded px-1.5 py-0.5 text-[10px]" />
          <button onClick={addHabit} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500 text-white font-bold">+</button>
        </div>
      )}
    </StickyNote>
  );
}

export default function BujoSidebar({ month, side = 'left', themeNotes }) {
  const [sidebar, setSidebar] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper: find a theme-overridden title/color for a default note id
  const noteOverride = (id, fallbackTitle, fallbackColor) => {
    const ov = (themeNotes || []).find((n) => n?.id === id);
    return {
      title: (ov && ov.title) ? ov.title : fallbackTitle,
      color: (ov && ov.color) ? ov.color : fallbackColor,
    };
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/bujo-cal/sidebar`, { params: { month }, withCredentials: true });
      setSidebar(r.data || {});
    } catch (e) {
      toast.error('Kunde inte ladda bujo-sidopanel');
    } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const update = async (key, value) => {
    setSidebar((s) => ({ ...s, [key]: value }));   // optimistic
    try {
      await axios.post(`${API}/bujo-cal/sidebar`, { month, key, value }, { withCredentials: true });
    } catch {
      toast.error('Kunde inte spara');
      load();
    }
  };

  if (loading || !sidebar) return null;

  if (side === 'left') {
    const pri = noteOverride('priorities', '🎯 Top Priorities', 'pink');
    const goals = noteOverride('goals', "This Month's Goals", 'violet');
    const note = noteOverride('note', 'Note to self', 'yellow');
    const remember = noteOverride('remember', 'Remember', 'rose');
    return (
      <div className="space-y-4 px-1 py-2" data-testid="bujo-sidebar-left">
        <StickyNote color={pri.color} tape={pri.color} title={pri.title} testid="bujo-priorities">
          <EditableList items={sidebar.top_priorities} onSave={(v) => update('top_priorities', v)} max={5} placeholder="prioritet…" emoji="1." />
        </StickyNote>
        <StickyNote color={goals.color} tape={goals.color} title={goals.title} testid="bujo-goals">
          <EditableList items={sidebar.this_month_goals} onSave={(v) => update('this_month_goals', v)} max={6} placeholder="mål…" emoji="☆" />
        </StickyNote>
        <StickyNote color={note.color} tape={note.color} title={note.title} testid="bujo-note">
          <EditableText value={sidebar.note_to_self} onSave={(v) => update('note_to_self', v)} placeholder="Slow progress is still progress 💛" rows={3} />
        </StickyNote>
        <StickyNote color={remember.color} tape={remember.color} title={remember.title} testid="bujo-remember">
          <EditableText value={sidebar.remember} onSave={(v) => update('remember', v)} placeholder="Små steg varje dag…" rows={3} />
        </StickyNote>
      </div>
    );
  }
  const sc = noteOverride('selfcare', '✓ Self Care Ideas', 'sky');
  const bk = noteOverride('books', '📚 Books to Read', 'violet');
  const gr = noteOverride('grateful', 'Grateful for', 'yellow');
  return (
    <div className="space-y-4 px-1 py-2" data-testid="bujo-sidebar-right">
      <StickyNote color={sc.color} tape={sc.color} title={sc.title} testid="bujo-selfcare">
        <EditableList items={sidebar.self_care_ideas} onSave={(v) => update('self_care_ideas', v)} max={6} placeholder="idé…" emoji="✓" />
      </StickyNote>
      <StickyNote color={bk.color} tape={bk.color} title={bk.title} testid="bujo-books">
        <EditableList items={sidebar.books_to_read} onSave={(v) => update('books_to_read', v)} max={5} placeholder="bok…" emoji="✓" />
      </StickyNote>
      <HabitsTracker month={month} sidebar={sidebar} refresh={load} />
      <StickyNote color={gr.color} tape={gr.color} title={gr.title} testid="bujo-grateful">
        <EditableText value={sidebar.grateful_for} onSave={(v) => update('grateful_for', v)} placeholder="god hälsa, kärlek, nya möjligheter…" rows={3} />
      </StickyNote>
    </div>
  );
}

export const BujoPaperBg = ({ children, className = '' }) => (
  <div
    className={`relative rounded-3xl shadow-inner ${className}`}
    style={{ background: PAPER_BG, backgroundAttachment: 'fixed' }}
  >
    {children}
  </div>
);
